import config, { Feature, isFeatureEnabled } from "../config";
import { BehalfOfRelation } from "../store/enums/BehalfOfRelation";
import { Person } from "../store/models/Person";
import { formatDate } from "../utils/date";
import { getStateName } from "../utils/stateAbbreviations";
import { formatSSN } from "../utils/string";
import { ICreateCsvDependent, ICreateCsvPayload } from "./interfaces/ICreateCsvPayload";
import { useAppStore } from "./useAppStore";
import { useDependentStore } from "./useDependentStore";
import { useEligibilityStore } from "./useEligibilityStore";
import useGetClassInfo from "./useGetClassInfo";
import { useReviewStore } from "./useReviewStore";
import { useSubscriberStore } from "./useSubscriberStore";

const useCreateCsvPayload = () => {
  const {
    eligibilityStore: { isUnder19, isBetween26to29, qualifyingLifeEvent, pediatricDental, relationToSubscriber, isEnrollingOnBehalf, broker, effectiveStartDate },
  } = useEligibilityStore();
  const {
    subscriberStore: { subscriberInfo, homeAddress, mailingAddress, homePhone, mailingPhone, email, isMailingSameAsHome, donateLifeOptIn },
  } = useSubscriberStore();
  const {
    dependentStore: { dependents, spouse },
  } = useDependentStore();
  const {
    appStore: { planInfo },
  } = useAppStore();

  const {
    reviewSelectionStore: { isDirectPurchaseReminder, isBenefitsAndCoverage, isSBCDocument, SBCDocumentUrl }
  } = useReviewStore();

  const { rateClassObj, getPlanRate } = useGetClassInfo();

  const hasBroker = isEnrollingOnBehalf && relationToSubscriber === BehalfOfRelation.Broker;

  const createPayload = () => {
    const rateClass = rateClassObj();
    const totalPlanRate = getPlanRate();
    const rateClassVerification = `${rateClass?.value} ${rateClass?.name}`;
    const result = {
      under_age_19: isUnder19,
      age_65_older: false,
      age_26_30: isBetween26to29,
      special_elig_cert: qualifyingLifeEvent.label || "",
      special_elig_event: qualifyingLifeEvent.date ? formatDate(qualifyingLifeEvent.date) : "",
      direct_purchase_reminder: isDirectPurchaseReminder,
      plan_type: planInfo?.planType || "",
      coplan_id: planInfo?.coPlan || "",
      ratebridge: planInfo?.rateBridge || "",
      plan_state: planInfo?.planState || "",
      plan_exchange: planInfo?.exchange || "",
      plan_metal_level: planInfo?.metalLevel || "",
      plan_availability: planInfo?.availability || "",
      rate_class_verification: rateClassVerification,
      pediatric_dental_rate: rateClass?.pediatricDentalRate || "0.00",
      dental_planid: planInfo?.dentalPlanId || "",
      already_have_pediatric_dental_coverage: pediatricDental.didUserVerify,
      company_providing_pediatric_dental_policy: pediatricDental.company || "",
      policy_number: pediatricDental.policyNumber || "",
      requested_start_date: effectiveStartDate ? formatDate(effectiveStartDate) : "",
      monthly_medical_plan_premium: rateClass?.value || "",
      monthly_pediatric_dental_plan_premium: "",
      total_monthly_premium: totalPlanRate || "",
      prefix: "",
      first_name: subscriberInfo.firstName || "",
      middle_initial: subscriberInfo.middleName || "",
      last_name: subscriberInfo.lastName || "",
      suffix: subscriberInfo.suffix || "",
      gender: subscriberInfo.gender || "",
      date_of_birth: subscriberInfo.birthDate ? formatDate(subscriberInfo.birthDate) : "",
      ssn: subscriberInfo.ssn ? formatSSN(subscriberInfo.ssn) : "",
      address_1: homeAddress.address1 || "",
      address_2: homeAddress.address2 || "",
      city: homeAddress.city || "",
      state: getStateName(homeAddress.state || ""),
      zip_code: homeAddress.zip || "",
      county: homeAddress.county || "",
      home_phone: homePhone || "",
      mobile_phone: homePhone || "",
      email_address: email || "",
      is_same_billing_address: isMailingSameAsHome ? 'Yes' : 'No',
      billing_prefix: "",
      billing_first_name: subscriberInfo.firstName || "",
      billing_middle_initial: subscriberInfo.middleName || "",
      billing_last_name: subscriberInfo.lastName || "",
      billing_suffix: subscriberInfo.suffix || "",
      billing_email: email || "",
      billing_address_1: (isMailingSameAsHome ? homeAddress.address1 : mailingAddress.address1) || "",
      billing_address_2: (isMailingSameAsHome ? homeAddress.address2 : mailingAddress.address2) || "",
      billing_city: (isMailingSameAsHome ? homeAddress.city : mailingAddress.city) || "",
      billing_state: (isMailingSameAsHome ? homeAddress.state : mailingAddress.state) || "",
      billing_zip_code: (isMailingSameAsHome ? homeAddress.zip : mailingAddress.zip) || "",
      billing_county: (isMailingSameAsHome ? homeAddress.county : mailingAddress.county) || "",
      billing_home_phone: (isMailingSameAsHome ? homePhone : mailingPhone) || "", 
      billing_mobile_phone: "",
      payment_info: "",
      broker_information: hasBroker ? 'iambroker' : 'nobroker',
      broker_first_name: hasBroker ? broker.firstName : "",
      broker_last_name: hasBroker ? broker.lastName : "",
      agency_name: hasBroker ? broker.agencyName : "",
      broker_phone_number: "518-555-5555",
      broker_email_address: hasBroker ? broker.email : "",
      mvp_agency_number: hasBroker ? broker.mvpAgencyNumber : "",
      customer_name: `${subscriberInfo.firstName} ${subscriberInfo.lastName}`,
      benefits_summary_attestation: isBenefitsAndCoverage,
      authorization_of_direct_payment: isDirectPurchaseReminder,
      emailSBC: isSBCDocument,
      sbc_document_url: sanitizeForWAF(SBCDocumentUrl) || "",
      dependents: convertToCsvDependent(dependents, spouse),
      healthsparqUrl: "",
      antiFraud: true,
      plan_id: planInfo?.planId || "",
      plan_name: planInfo?.planName || "",
      plan_county: planInfo?.planCounty || "",
      plan_zipcode: planInfo?.planZipcode || "",
      primary_care_visit: planInfo?.primaryCareVisit || "",
      specialist_visit: planInfo?.specialistVisit || "",
      rx_coverage_generic: planInfo?.rxCoverageGeneric || "",
      rx_coverage_preferred: planInfo?.rxCoveragePreferred || "",
      rx_coverage_nonpreferred: planInfo?.rxCoverageNonPreferred || "",
      annual_inn_deductible: planInfo?.annualInnDeductible || "",
      annual_oon_deductible: planInfo?.annualOonDeductible || "",
      annual_inn_oop_limits: planInfo?.annualInnOopLimits || "",
      annual_oon_oop_limits: planInfo?.annualOonOopLimits || "",
      rate_class_single: planInfo?.rateClasses.Single.value || "",
      rate_class_double: planInfo?.rateClasses.Double.value || "",
      rate_class_parent: planInfo?.rateClasses.Parent.value || "",
      rate_class_family: planInfo?.rateClasses.Family.value || "",
      primary_care_visit_le: planInfo?.primaryCareVisitLe || "",
      specialist_visit_le: planInfo?.specialistVisitLe || "",
      enrollment_period_type: planInfo?.enrollmentPeriodType || "",
      pediatric_dental_policy_number: pediatricDental.policyNumber || "",
      effective_Start_Date: "",
      payment_url: sanitizeForWAF(config.MEMBER_PAYMENT_URL) || ""
    } as ICreateCsvPayload;

    // If feature is not enabled, donatelife will be undefined and should not be included in the payload. If the feature is enabled, it should be included with value "Yes", "Skip this question", or "" based on the user's selection.
    if (isFeatureEnabled(Feature.DonateLife)) {
      if (donateLifeOptIn === true) {
        result.donatelife = "Yes";
      } else if (donateLifeOptIn === false) {
        result.donatelife = "Skip this question";
      } else {
        result.donatelife = "";
      }
    }

    return result;
  }
  return { createPayload };
};

// Removes protocol from url to prevent WAF blocking
const sanitizeForWAF   = (url: string): string => {
  if (!url) return url;
  return url.replace(/^https?:\/\//, '');
}

const convertToCsvDependent = (dependents: Person[], spouse: Person | null): ICreateCsvDependent[] => {
  const dependentsArray: ICreateCsvDependent[] = dependents.map((dependent: Person) => {
    return {
      relationship: 'child',
      prefix: '',
      first_name: dependent.firstName,
      middle_initial: dependent.middleName,
      last_name: dependent.lastName,
      suffix: dependent.suffix,
      gender: dependent.gender || "",
      ssn: dependent.ssn ? formatSSN(dependent.ssn) : "",
      date_of_birth: dependent.birthDate ? formatDate(dependent.birthDate) : ""
    }
  })

  if (spouse) {
    dependentsArray.unshift({
      relationship: 'spouse',
      prefix: '',
      first_name: spouse.firstName,
      middle_initial: spouse.middleName,
      last_name: spouse.lastName,
      suffix: spouse.suffix,
      gender: spouse.gender || "",
      ssn: spouse.ssn ? formatSSN(spouse.ssn) : "",
      date_of_birth: spouse.birthDate ? formatDate(spouse.birthDate) : ""
    })
  }

  return dependentsArray;
}

export default useCreateCsvPayload;
